import { useState } from "react";
import { Alert } from "react-bootstrap";
import { Column } from "react-table";
import {
  GetLicenceProfileSearchFetchUrl,
  IAuthorisationTypesList,
  ILicenceSearchList,
} from "../../api/ILicenceProfileSearch";
import { Grid } from "../../leap-components/Grid";
import LeapSearch from "./Search";
import { CreateStaticSelectColumnFilter } from "../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import {
  GetLicenceProfileUrl,
  GetLicenceVersionUrl,
} from "../../external-apps/Config";
import { InformationIcon } from "../../leap-components/icons/LeapIcons";

interface ISearchResultRow {
  licence_name: string;
  profile_number: string;
  organisation: string;
  active_application: string;
  county: string;
  active_licence_id: string;
  active_licence_regno: string;
  active_application_licence_id: string;
  active_application_licence_regno: string;
  active_licence_type: string;
}

interface IBasicSearchProps {
  authorisationTypes: IAuthorisationTypesList;
}

const alertColor = "secondary";

const LeapAlert = () => (
  <Alert variant={alertColor} dismissible>
    <div className="alert-icon">
      <InformationIcon />
    </div>
    <div className="alert-message">
      <p>
        This is a global search function that allows users to search EPA licence
        applications and authorisation records. It provides access to
        summary-level information for each authorisation. Use this search to
        locate both applications and authorisations across licensing regimes.
      </p>
      <p>
        The search function allows users to find licence applications and
        authorisations issued by the EPA. It provides summary-level information
        rather than detailed versions of each authorisation.
      </p>
    </div>
  </Alert>
);

const GetLicenceProfileUrlFromRow = (row: any) => {
  return GetLicenceProfileUrl(
    row.original.active_licence_type,
    row.original.profile_number,
    row.original.active_licence_id,
    row.original.active_licence_regno
  );
};

const GetLicenceApplicationUrlFromRow = (row: any) => {
  return GetLicenceVersionUrl(
    row.original.active_licence_type,
    row.original.profile_number,
    row.original.active_application_licence_id,
    row.original.active_application_licence_regno
  );
};

const BasicSearch = ({ authorisationTypes }: IBasicSearchProps) => {
  const [searchValue, setSearchValue] = useState("");

  const activeApplicationFilter = ["Yes", "No"];

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  const tableColumnsBig: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Number",
      accessor: "profile_number",
      Filter: false,
      filter: "licence_number",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceProfileUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
    },
    {
      Header: "Name",
      accessor: "licence_name",
      Filter: false,
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisation_type",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Active Application?",
      accessor: "active_application",
      Filter: CreateStaticSelectColumnFilter(activeApplicationFilter),
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceApplicationUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: false,
    },
  ];

  let fetchUrlFn = (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any
  ) => {
    return GetLicenceProfileSearchFetchUrl(
      pageNo,
      recordsPerPage,
      searchText,
      filters
    );
  };

  let formatResponseFunction = (searchResponse: ILicenceSearchList) => {
    const array = searchResponse.list.map((result) => {
      return {
        licence_name: result.name,
        active_licence_type: result.active_licence_type,
        active_licence_id: result.active_licence_id,
        active_licence_regno: result.active_licence_regno,
        profile_number: result.profile_number,
        organisation: result.organisation_name,
        authorisation_type: result.active_licence_type,
        active_application: result.active_applicatio_number,
        active_application_licence_id: result.active_application_licence_id,
        active_application_licence_regno:
          result.active_application_licence_regno,
        county: result.county,
      } as ISearchResultRow;
    });

    return {
      list: array,
      length: searchResponse.length,
      page: searchResponse.page,
      count: searchResponse.count,
    };
  };

  const results = (
    <div>
      <div className="d-md-none">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
        />
      </div>
      <div className="d-none d-md-block">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
        />
      </div>
    </div>
  );

  const searchContent = (
    <div>
      <LeapAlert />
      <LeapSearch searchChangedFn={searchChangedFn} />
      {results}
    </div>
  );

  return searchContent;
};

export default BasicSearch;
